import { useSyncExternalStore } from "react";
import {
  getPhotoUploadRecord,
  getPhotoUploadRecordsByOwner,
  markPhotoUploadDeleting,
  putPhotoUploadRecordIfCurrent,
  restorePhotoUploadAfterDeleteFailure,
  tombstonePhotoUploadRecord,
  updatePhotoUploadRecord,
} from "./photoUploadStore.js";
import { fetchWithCustomerRequestAuth } from "../../../../../helpers/customerRequestAuth.js";
import { requestImageUploadPermit } from "./uploadConcurrencyGate.js";

// A single request is intentionally used here. On slow mobile uplinks two
// parallel multipart uploads compete for the same bandwidth and can both hit
// their deadline even though either request would finish on its own.
const MAX_PARALLEL_UPLOADS = 1;
const MIN_UPLOAD_TIMEOUT_MS = 5 * 60_000;
const MAX_UPLOAD_TIMEOUT_MS = 15 * 60_000;
const MIN_EXPECTED_UPLOAD_BYTES_PER_SECOND = 4 * 1024;
const UPLOAD_TIMEOUT_OVERHEAD_MS = 60_000;
const RETRY_DELAYS_MS = [3000, 10000, 30000, 60000];
const HIDDEN_STATES = new Set(["confirmed", "deleted", "superseded"]);
const JOB_STOP_STATES = new Set(["deleting", ...HIDDEN_STATES]);
const NAVIGATION_STATES = new Set([
  "pending",
  "committed",
  "deleting",
]);
const SERVER_EMPTY_SNAPSHOT = Object.freeze({});
const SERVER_REGISTRY_STATUS = Object.freeze({
  hydrated: false,
  ownerEmail: null,
  persistenceAvailable: true,
  storageDefinitelyUnavailable: false,
});

const listeners = new Set();
const records = new Map();
const jobs = new Map();
const retryTimers = new Map();
const ownedPreviewUrls = new Map();
const queue = [];

let snapshot = Object.freeze({});
let registryStatus = Object.freeze({
  hydrated: false,
  ownerEmail: null,
  persistenceAvailable: true,
  storageDefinitelyUnavailable: false,
});
let apiBaseUrl = "";
let currentOwnerEmail = null;
let hydrationPromise = null;
let hydrationGeneration = 0;
let activeUploads = 0;
let onlineListenerInstalled = false;
let uploadTimeoutOverrideMs = null;
let loadPhotoUploadRecordsByOwner = getPhotoUploadRecordsByOwner;
let persistPhotoUploadRecord = putPhotoUploadRecordIfCurrent;

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

export function makePhotoUploadKey(vehicleId, category, step) {
  return `${String(vehicleId || "")}::${String(category || "")}::${String(
    step || ""
  )}`;
}

function emit() {
  listeners.forEach((listener) => listener());
}

function publishEntry(key, entry) {
  snapshot = Object.freeze({ ...snapshot, [key]: Object.freeze(entry) });
  emit();
}

function removeEntry(key) {
  if (!snapshot[key]) return;
  const next = { ...snapshot };
  delete next[key];
  snapshot = Object.freeze(next);
  emit();
}

function publishRegistryStatus(patch) {
  registryStatus = Object.freeze({ ...registryStatus, ...patch });
  emit();
}

function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getPhotoUploadSnapshot() {
  return snapshot;
}

export function getPhotoUploadRegistryStatus() {
  return registryStatus;
}

export function usePhotoUploadSnapshot() {
  return useSyncExternalStore(
    subscribe,
    getPhotoUploadSnapshot,
    () => SERVER_EMPTY_SNAPSHOT
  );
}

export function usePhotoUploadRegistryStatus() {
  return useSyncExternalStore(
    subscribe,
    getPhotoUploadRegistryStatus,
    () => SERVER_REGISTRY_STATUS
  );
}

function createUploadId() {
  return (
    globalThis.crypto?.randomUUID?.() ||
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  );
}

function createDeferred() {
  let resolve;
  const promise = new Promise((resolvePromise) => {
    resolve = resolvePromise;
  });
  return { promise, resolve, settled: false };
}

function settle(job, result) {
  if (job.completion.settled) return;
  job.completion.settled = true;
  job.completion.resolve(result);
}

function entryFromRecord(record, previewUrl = null) {
  return {
    key: record.key,
    ownerEmail: record.ownerEmail,
    vehicleId: record.vehicleId,
    category: record.category,
    step: record.step,
    fileName: record.fileName || null,
    uploadId: record.uploadId,
    capturedAt: record.capturedAt,
    generation: record.generation || null,
    state: record.state,
    phase: record.phase || null,
    attempts: Number(record.attempts || 0),
    previewUrl: previewUrl || record.previewUrl || null,
    hasLocalBlob: Boolean(record.blob),
    imageUrl: record.imageUrl || null,
    previousImageUrl: record.previousImageUrl || null,
    serverRequestStarted: record.serverRequestStarted === true,
    persisted: record.persisted !== false,
    retryable: record.retryable !== false,
    errorName: record.errorName || null,
    errorCode: record.errorCode || null,
    errorMessage: record.errorMessage || null,
    httpStatus: Number(record.httpStatus || 0) || null,
    currentUploadId: record.currentUploadId || null,
    currentGeneration: record.currentGeneration || null,
  };
}

function createPreviewUrl(record) {
  if (!record?.blob || !globalThis.URL?.createObjectURL) return null;
  const previous = ownedPreviewUrls.get(record.key);
  if (previous) globalThis.URL.revokeObjectURL?.(previous);
  const next = globalThis.URL.createObjectURL(record.blob);
  ownedPreviewUrls.set(record.key, next);
  return next;
}

function releasePreviewUrl(key) {
  const previewUrl = ownedPreviewUrls.get(key);
  if (previewUrl) globalThis.URL?.revokeObjectURL?.(previewUrl);
  ownedPreviewUrls.delete(key);
}

function isJobCurrent(job) {
  return (
    jobs.get(job.key) === job &&
    records.get(job.key)?.uploadId === job.record.uploadId &&
    !JOB_STOP_STATES.has(records.get(job.key)?.state)
  );
}

function clearRetryTimer(key) {
  const timer = retryTimers.get(key);
  if (timer) clearTimeout(timer);
  retryTimers.delete(key);
}

function acquireUploadSlot(job) {
  if (!job) return Promise.resolve(false);
  if (job.slotHeld) {
    return job.uploadPermit?.promise || Promise.resolve(true);
  }
  job.slotHeld = true;
  activeUploads += 1;
  job.uploadPermit = requestImageUploadPermit(job);
  return job.uploadPermit.promise;
}

function releaseUploadSlot(job) {
  if (!job?.slotHeld) return false;
  job.slotHeld = false;
  const uploadPermit = job.uploadPermit;
  job.uploadPermit = null;
  uploadPermit?.cancel();
  activeUploads = Math.max(0, activeUploads - 1);
  return true;
}

function abortJob(job, result = { ok: false, superseded: true }) {
  if (!job) return;
  job.controller?.abort();
  job.cancelRequest?.();
  clearTimeout(job.timeoutId);
  job.timeoutId = null;
  clearRetryTimer(job.key);
  for (let index = queue.length - 1; index >= 0; index -= 1) {
    if (queue[index] === job) queue.splice(index, 1);
  }
  settle(job, result);
  if (releaseUploadSlot(job)) scheduleQueue();
}

async function parseResponseBody(response) {
  const contentType = response.headers?.get?.("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json().catch(() => ({}));
  }
  const text = await response.text().catch(() => "");
  return text ? { error: text } : {};
}

function parseRetryAfter(response) {
  const value = response.headers?.get?.("retry-after");
  if (!value) return 0;
  const seconds = Number(value);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);
  const date = Date.parse(value);
  return Number.isFinite(date) ? Math.max(0, date - Date.now()) : 0;
}

function createUploadError(response, result) {
  const error = new Error(
    result?.error || result?.message || "Foto konnte nicht hochgeladen werden."
  );
  const hasCurrentImageUrl =
    Object.prototype.hasOwnProperty.call(result || {}, "currentImageUrl") &&
    (result?.currentImageUrl === null ||
      typeof result?.currentImageUrl === "string");
  error.name = "PhotoUploadError";
  error.status = response.status;
  error.code = result?.code || null;
  error.currentUploadId = result?.currentUploadId || null;
  error.currentGeneration = Number(result?.currentGeneration || 0) || null;
  error.currentStatus = result?.currentStatus || null;
  error.hasCurrentImageUrl = hasCurrentImageUrl;
  error.currentImageUrl = hasCurrentImageUrl
    ? typeof result.currentImageUrl === "string"
      ? result.currentImageUrl.trim() || null
      : null
    : undefined;
  error.externalReplacement = result?.externalReplacement === true;
  error.retryAfterMs = parseRetryAfter(response);
  error.retryable =
    response.status === 401 ||
    response.status === 408 ||
    response.status === 425 ||
    response.status === 429 ||
    response.status >= 500;
  return error;
}

function buildUploadForm(record) {
  const file = new File(
    [record.blob],
    record.fileName || `${record.step}.jpg`,
    { type: record.contentType || record.blob?.type || "image/jpeg" }
  );
  const form = new FormData();
  form.append("file", file);
  form.append("email", record.ownerEmail);
  form.append("id", record.vehicleId);
  form.append("type", record.step);
  form.append("category", record.category);
  form.append("imageSource", record.imageSource || "camera");
  form.append("uploadId", record.uploadId);
  form.append("capturedAt", String(record.capturedAt));
  if (record.generation) form.append("uploadGeneration", String(record.generation));
  return form;
}

function buildReserveBody(record) {
  const hasObservedServerState = [
    "observedUploadId",
    "observedGeneration",
    "observedBaseMainUrl",
  ].every((field) => Object.prototype.hasOwnProperty.call(record, field));
  return JSON.stringify({
    email: record.ownerEmail,
    id: record.vehicleId,
    type: record.step,
    category: record.category,
    imageSource: record.imageSource || "camera",
    uploadId: record.uploadId,
    capturedAt: record.capturedAt,
    ...(hasObservedServerState
      ? {
          observedUploadId: record.observedUploadId ?? null,
          observedGeneration: record.observedGeneration ?? null,
          observedBaseMainUrl: record.observedBaseMainUrl ?? null,
        }
      : {}),
    ...(record.generation
      ? { uploadGeneration: Number(record.generation) }
      : {}),
  });
}

function getValidatedReservation(record, result) {
  if (result?.uploadId !== record.uploadId) {
    const error = new Error("Der Server hat eine falsche Upload-ID reserviert.");
    error.name = "PhotoUploadProtocolError";
    error.retryable = true;
    throw error;
  }
  const generation = Number(result?.generation);
  if (!Number.isSafeInteger(generation) || generation <= 0) {
    const error = new Error(
      "Der Server hat keine gültige Upload-Generation reserviert."
    );
    error.name = "PhotoUploadProtocolError";
    error.retryable = true;
    throw error;
  }
  if (
    record.generation &&
    Number(record.generation) !== generation
  ) {
    const error = new Error(
      "Der Server hat eine andere Upload-Generation reserviert."
    );
    error.name = "PhotoUploadProtocolError";
    error.retryable = true;
    throw error;
  }

  const status = String(result?.status || "");
  if (!["pending", "main_uploaded"].includes(status)) {
    const error = new Error("Der Server hat den Upload nicht reserviert.");
    error.name = "PhotoUploadProtocolError";
    error.retryable = true;
    throw error;
  }

  const imageUrl =
    typeof result?.file?.url === "string" ? result.file.url.trim() : "";
  if (status === "main_uploaded") {
    try {
      if (!["http:", "https:"].includes(new URL(imageUrl).protocol)) {
        throw new Error("invalid protocol");
      }
    } catch {
      const error = new Error(
        "Der Server meldet einen fertigen Upload ohne gültige Bild-URL."
      );
      error.name = "PhotoUploadProtocolError";
      error.retryable = true;
      throw error;
    }
  }

  return {
    generation,
    completed: status === "main_uploaded",
    imageUrl: imageUrl || null,
  };
}

async function reserveRecord(record, signal) {
  const baseUrl = String(apiBaseUrl || record.apiBaseUrl || "").replace(
    /\/+$/,
    ""
  );
  if (!baseUrl) {
    const error = new Error("Backend-Adresse für den Foto-Upload fehlt.");
    error.retryable = true;
    throw error;
  }

  const response = await fetchWithCustomerRequestAuth(
    `${baseUrl}/fahrzeug-profil/car-images/upload/reserve`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: buildReserveBody(record),
      signal,
    }
  );
  const result = await parseResponseBody(response);
  if (!response.ok) throw createUploadError(response, result);
  return getValidatedReservation(record, result);
}

async function uploadRecord(record, signal) {
  if (!record.blob) {
    const error = new Error("Das lokal gespeicherte Foto fehlt.");
    error.retryable = false;
    throw error;
  }
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    const error = new Error("Keine Internetverbindung.");
    error.name = "OfflinePhotoUploadError";
    error.retryable = true;
    throw error;
  }

  const baseUrl = String(apiBaseUrl || record.apiBaseUrl || "").replace(/\/+$/, "");
  if (!baseUrl) {
    const error = new Error("Backend-Adresse für den Foto-Upload fehlt.");
    error.retryable = true;
    throw error;
  }

  const response = await fetchWithCustomerRequestAuth(
    `${baseUrl}/fahrzeug-profil/car-images/upload`,
    {
      method: "POST",
      body: buildUploadForm(record),
      signal,
    }
  );
  const result = await parseResponseBody(response);
  if (!response.ok) throw createUploadError(response, result);

  const imageUrl =
    typeof result?.file?.url === "string" ? result.file.url.trim() : "";
  let validImageUrl = false;
  try {
    const parsedImageUrl = new URL(imageUrl);
    validImageUrl = ["http:", "https:"].includes(parsedImageUrl.protocol);
  } catch {}
  if (!validImageUrl) {
    const error = new Error("Upload erfolgreich, aber keine Bild-URL erhalten.");
    error.name = "PhotoUploadProtocolError";
    // The atomic server write may already have succeeded while its response was
    // truncated. Retrying the same uploadId is idempotent and Firestore can
    // independently confirm the completed upload in the meantime.
    error.retryable = true;
    throw error;
  }
  if (result?.uploadId !== record.uploadId) {
    const error = new Error("Der Server hat eine falsche Upload-ID bestätigt.");
    error.name = "PhotoUploadProtocolError";
    error.retryable = true;
    throw error;
  }
  const generation = Number(result?.generation);
  if (!Number.isSafeInteger(generation) || generation <= 0) {
    const error = new Error(
      "Der Server hat keine gültige Upload-Generation bestätigt."
    );
    error.name = "PhotoUploadProtocolError";
    error.retryable = true;
    throw error;
  }

  return {
    imageUrl,
    generation,
  };
}

function getUploadTimeoutMs(record) {
  if (uploadTimeoutOverrideMs !== null) return uploadTimeoutOverrideMs;
  const blobSize = Math.max(0, Number(record?.blob?.size || 0));
  const sizeAwareTimeout =
    blobSize > 0
      ? Math.ceil(
          (blobSize / MIN_EXPECTED_UPLOAD_BYTES_PER_SECOND) * 1000
        ) + UPLOAD_TIMEOUT_OVERHEAD_MS
      : 0;
  return Math.min(
    MAX_UPLOAD_TIMEOUT_MS,
    Math.max(MIN_UPLOAD_TIMEOUT_MS, sizeAwareTimeout)
  );
}

function uploadRecordWithDeadline(job, record) {
  const timeoutMs = getUploadTimeoutMs(record);
  return new Promise((resolve, reject) => {
    let requestSettled = false;
    const finish = (handler, value) => {
      if (requestSettled) return;
      requestSettled = true;
      clearTimeout(job.timeoutId);
      job.timeoutId = null;
      job.cancelRequest = null;
      handler(value);
    };

    job.timeoutId = setTimeout(() => {
      if (requestSettled) return;
      job.timedOut = true;
      job.controller?.abort();
      const error = new Error("Der Foto-Upload hat zu lange gedauert.");
      error.name = "PhotoUploadTimeoutError";
      error.retryable = true;
      finish(reject, error);
    }, timeoutMs);
    job.cancelRequest = () => {
      const error = new Error("Der Foto-Upload wurde abgebrochen.");
      error.name = "AbortError";
      error.retryable = false;
      finish(reject, error);
    };

    // The explicit Promise boundary is important: some WebKit versions have
    // left fetch pending even after AbortController.abort(). The deadline still
    // settles this worker and frees its queue slot; a late response is ignored.
    Promise.resolve()
      .then(async () => {
        const reservation = await reserveRecord(
          record,
          job.controller.signal
        );
        const reserved = await persistPatch(record, {
          generation: reservation.generation,
          phase: reservation.completed ? "committing" : "uploading",
        });
        if (!reserved || !isJobCurrent(job)) {
          const error = new Error(
            "Der Foto-Upload wurde durch einen neueren Upload ersetzt."
          );
          error.name = "PhotoUploadPersistenceCasError";
          error.retryable = false;
          throw error;
        }
        publishEntry(
          record.key,
          entryFromRecord(record, snapshot[record.key]?.previewUrl)
        );
        if (reservation.completed) {
          return {
            imageUrl: reservation.imageUrl,
            generation: reservation.generation,
          };
        }
        return uploadRecord(record, job.controller.signal);
      })
      .then(
        (result) => finish(resolve, result),
        (error) => finish(reject, error)
      );
  });
}

async function persistPatch(record, patch) {
  const updatedAt = Date.now();
  if (record.persisted === false) {
    Object.assign(record, patch, { updatedAt });
    records.set(record.key, record);
    return true;
  }

  try {
    const updated = await updatePhotoUploadRecord(
      record.key,
      record.uploadId,
      { ...patch, updatedAt }
    );
    if (!updated) return false;
    Object.assign(record, patch, { updatedAt });
    records.set(record.key, record);
    return true;
  } catch (error) {
    console.error("Foto-Upload-Status konnte nicht gespeichert werden:", error);
    publishRegistryStatus({ persistenceAvailable: false });
    Object.assign(record, patch, { updatedAt });
    record.persisted = false;
    records.set(record.key, record);
    return true;
  }
}

function adoptStoredRecord(storedRecord) {
  if (
    !storedRecord ||
    storedRecord.ownerEmail !== currentOwnerEmail
  ) {
    return false;
  }

  const memoryRecord = records.get(storedRecord.key);
  if (
    memoryRecord?.uploadId === storedRecord.uploadId &&
    Number(memoryRecord.updatedAt || 0) > Number(storedRecord.updatedAt || 0)
  ) {
    return false;
  }
  if (memoryRecord && memoryRecord.uploadId !== storedRecord.uploadId) {
    const storedCapturedAt = Number(storedRecord.capturedAt || 0);
    const memoryCapturedAt = Number(memoryRecord.capturedAt || 0);
    if (
      storedCapturedAt < memoryCapturedAt ||
      (storedCapturedAt === memoryCapturedAt &&
        String(storedRecord.uploadId) < String(memoryRecord.uploadId))
    ) {
      return false;
    }
  }

  const existingJob = jobs.get(storedRecord.key);
  if (
    existingJob?.record?.uploadId === storedRecord.uploadId &&
    storedRecord.state === "pending"
  ) {
    return true;
  }
  if (existingJob) {
    abortJob(existingJob);
    if (jobs.get(storedRecord.key) === existingJob) {
      jobs.delete(storedRecord.key);
    }
  }

  const nextRecord = { ...storedRecord, persisted: true };
  records.set(nextRecord.key, nextRecord);
  if (HIDDEN_STATES.has(nextRecord.state)) {
    releasePreviewUrl(nextRecord.key);
    removeEntry(nextRecord.key);
    return true;
  }
  const previewUrl = createPreviewUrl(nextRecord);
  publishEntry(nextRecord.key, entryFromRecord(nextRecord, previewUrl));
  if (nextRecord.state === "pending") enqueueJob(createJob(nextRecord));
  return true;
}

async function persistVolatileRecords(ownerEmail) {
  const volatileRecords = Array.from(records.values()).filter(
    (record) =>
      record?.persisted === false &&
      record.ownerEmail === ownerEmail
  );

  for (const record of volatileRecords) {
    const persisted = await persistPhotoUploadRecord(record);
    const current = records.get(record.key);
    if (
      persisted &&
      current?.uploadId === record.uploadId &&
      current.persisted === false
    ) {
      current.persisted = true;
      const visibleEntry = snapshot[record.key];
      if (visibleEntry?.uploadId === record.uploadId) {
        publishEntry(
          record.key,
          entryFromRecord(current, visibleEntry.previewUrl || null)
        );
      }
      continue;
    }
    if (!persisted && current?.uploadId === record.uploadId) {
      adoptStoredRecord(await getPhotoUploadRecord(record.key));
    }
  }
}

async function reconcileAfterCasLoss(job) {
  clearRetryTimer(job.key);
  settle(job, { ok: false, superseded: true });
  if (jobs.get(job.key) === job) jobs.delete(job.key);

  if (records.get(job.key)?.uploadId === job.record.uploadId) {
    records.delete(job.key);
    releasePreviewUrl(job.key);
    removeEntry(job.key);
  }

  try {
    adoptStoredRecord(await getPhotoUploadRecord(job.key));
  } catch (error) {
    console.error("Neuerer Foto-Upload konnte nicht geladen werden:", error);
  }
}

function retryDelay(record, error) {
  const index = Math.min(
    Number(record.attempts || 0),
    RETRY_DELAYS_MS.length - 1
  );
  return Math.max(RETRY_DELAYS_MS[index], Number(error?.retryAfterMs || 0));
}

async function scheduleRetry(job, error, rebasePatch = null) {
  if (!isJobCurrent(job)) return;
  const record = job.record;
  const delay = retryDelay(record, error);
  const persisted = await persistPatch(record, {
    ...(rebasePatch || {}),
    state: "pending",
    phase: "retry_wait",
    attempts: Number(record.attempts || 0) + 1,
    retryable: true,
    errorName: error?.name || null,
    errorCode: error?.code || null,
    errorMessage: error?.message || "Upload wird erneut versucht.",
    httpStatus: Number(error?.status || 0) || null,
    nextRetryAt: Date.now() + delay,
  });
  if (!persisted) {
    await reconcileAfterCasLoss(job);
    return;
  }
  if (!isJobCurrent(job)) return;
  publishEntry(record.key, entryFromRecord(record, snapshot[record.key]?.previewUrl));
  clearRetryTimer(record.key);
  retryTimers.set(
    record.key,
    setTimeout(() => {
      retryTimers.delete(record.key);
      if (isJobCurrent(job)) enqueueJob(job);
    }, delay)
  );
}

function isStaleFromLocalPredecessor(record, error) {
  if (
    Number(record?.generation || 0) > 0 ||
    !record?.blob ||
    !record?.localPredecessorUploadId ||
    record.localPredecessorUploadId !== error?.currentUploadId ||
    !Number.isSafeInteger(Number(error?.currentGeneration)) ||
    Number(error.currentGeneration) <= 0 ||
    !["pending", "main_uploaded"].includes(error?.currentStatus) ||
    error?.externalReplacement === true
  ) {
    return false;
  }

  const knownGeneration = Number(record.localPredecessorGeneration || 0);
  return (
    knownGeneration <= 0 ||
    knownGeneration === Number(error.currentGeneration)
  );
}

function getLocalPredecessorRebasePatch(record, error) {
  if (
    !isStaleFromLocalPredecessor(record, error) ||
    error?.hasCurrentImageUrl !== true
  ) {
    return null;
  }

  const currentImageUrl = error.currentImageUrl ?? null;
  const alreadyObserved =
    record.observedUploadId === error.currentUploadId &&
    Number(record.observedGeneration || 0) ===
      Number(error.currentGeneration) &&
    (record.observedBaseMainUrl || null) === currentImageUrl;
  if (alreadyObserved) return null;

  return {
    generation: null,
    localPredecessorGeneration: Number(error.currentGeneration),
    observedUploadId: error.currentUploadId,
    observedGeneration: Number(error.currentGeneration),
    observedBaseMainUrl: currentImageUrl,
    previousImageUrl: currentImageUrl,
  };
}

async function runJob(job) {
  if (!isJobCurrent(job) || job.running) return;
  job.running = true;
  const uploadPermitGranted = acquireUploadSlot(job);
  const record = job.record;

  try {
    if (!(await uploadPermitGranted) || !isJobCurrent(job)) return;
    job.controller = new AbortController();
    job.timedOut = false;

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      const offlineError = new Error("Keine Internetverbindung.");
      offlineError.name = "OfflinePhotoUploadError";
      offlineError.retryable = true;
      throw offlineError;
    }

    const canStart = await persistPatch(record, {
      state: "pending",
      phase: "reserving",
      serverRequestStarted: true,
      errorName: null,
      errorCode: null,
      errorMessage: null,
      httpStatus: null,
    });
    if (!canStart || !isJobCurrent(job)) {
      if (!canStart) await reconcileAfterCasLoss(job);
      else settle(job, { ok: false, superseded: true });
      return;
    }
    publishEntry(record.key, entryFromRecord(record, snapshot[record.key]?.previewUrl));

    const result = await uploadRecordWithDeadline(job, record);
    if (releaseUploadSlot(job)) scheduleQueue();
    if (!isJobCurrent(job)) return;

    const committed = await persistPatch(record, {
      state: "committed",
      phase: null,
      blob: null,
      imageUrl: result.imageUrl,
      generation: result.generation,
      retryable: false,
      errorName: null,
      errorCode: null,
      errorMessage: null,
      httpStatus: null,
      nextRetryAt: null,
    });
    if (!committed) {
      await reconcileAfterCasLoss(job);
      return;
    }
    if (!isJobCurrent(job)) return;
    publishEntry(record.key, entryFromRecord(record, snapshot[record.key]?.previewUrl));
    settle(job, { ok: true, ...result });
    if (jobs.get(job.key) === job) jobs.delete(job.key);
  } catch (error) {
    if (releaseUploadSlot(job)) scheduleQueue();
    if (!isJobCurrent(job)) return;
    if (error?.name === "PhotoUploadPersistenceCasError") {
      await reconcileAfterCasLoss(job);
    } else if (error?.name === "PhotoUploadTimeoutError") {
      await scheduleRetry(job, error);
    } else if (error?.name === "AbortError") {
      settle(job, { ok: false, superseded: true });
    } else if (
      error?.status === 409 &&
      error?.code === "STALE_PHOTO_UPLOAD"
    ) {
      const predecessorRebasePatch = getLocalPredecessorRebasePatch(
        record,
        error
      );
      if (predecessorRebasePatch) {
        await scheduleRetry(job, error, predecessorRebasePatch);
        return;
      }
      // During a rolling backend deployment an older 409 response may not yet
      // contain currentImageUrl. Keep the newest local blob retryable instead
      // of incorrectly terminalising it as long as the conflicting upload is
      // exactly the in-flight local predecessor.
      if (
        isStaleFromLocalPredecessor(record, error) &&
        error?.hasCurrentImageUrl !== true
      ) {
        await scheduleRetry(job, error);
        return;
      }
      if (error.externalReplacement) {
        const confirmed = await persistPatch(record, {
          state: "confirmed",
          phase: null,
          blob: null,
          retryable: false,
          currentUploadId: error.currentUploadId,
          currentGeneration: error.currentGeneration,
          errorCode: error.code,
          errorMessage: null,
        });
        if (!confirmed) {
          await reconcileAfterCasLoss(job);
          return;
        }
        releasePreviewUrl(record.key);
        removeEntry(record.key);
        settle(job, { ok: false, externalReplacement: true });
        if (jobs.get(job.key) === job) jobs.delete(job.key);
        return;
      }
      if (error.currentStatus === "deleted") {
        const deleted = await persistPatch(record, {
          state: "deleted",
          phase: null,
          blob: null,
          retryable: false,
          currentUploadId: error.currentUploadId,
          currentGeneration: error.currentGeneration,
          errorCode: error.code,
          errorMessage: null,
        });
        if (!deleted) {
          await reconcileAfterCasLoss(job);
          return;
        }
        releasePreviewUrl(record.key);
        removeEntry(record.key);
        settle(job, { ok: false, deleted: true });
        if (jobs.get(job.key) === job) jobs.delete(job.key);
        return;
      }
      const superseded = await persistPatch(record, {
        state: "superseded",
        phase: null,
        blob: null,
        retryable: false,
        currentUploadId: error.currentUploadId,
        currentGeneration: error.currentGeneration,
        errorCode: error.code,
        errorMessage: null,
      });
      if (!superseded) {
        await reconcileAfterCasLoss(job);
        return;
      }
      releasePreviewUrl(record.key);
      removeEntry(record.key);
      settle(job, { ok: false, superseded: true });
      if (jobs.get(job.key) === job) jobs.delete(job.key);
    } else if (error?.retryable !== false) {
      await scheduleRetry(job, error);
    } else {
      const failed = await persistPatch(record, {
        state: "failed",
        phase: null,
        retryable: false,
        errorName: error?.name || null,
        errorCode: error?.code || null,
        errorMessage: error?.message || "Foto konnte nicht hochgeladen werden.",
        httpStatus: Number(error?.status || 0) || null,
      });
      if (!failed) {
        await reconcileAfterCasLoss(job);
        return;
      }
      publishEntry(record.key, entryFromRecord(record, snapshot[record.key]?.previewUrl));
      settle(job, { ok: false, error });
      if (jobs.get(job.key) === job) jobs.delete(job.key);
    }
  } finally {
    clearTimeout(job.timeoutId);
    job.timeoutId = null;
    job.controller = null;
    job.cancelRequest = null;
    job.timedOut = false;
    job.running = false;
    releaseUploadSlot(job);
    scheduleQueue();
  }
}

function scheduleQueue() {
  while (activeUploads < MAX_PARALLEL_UPLOADS && queue.length) {
    const job = queue.shift();
    if (!isJobCurrent(job) || job.running) continue;
    void runJob(job);
  }
}

function enqueueJob(job) {
  if (!isJobCurrent(job) || job.running || queue.includes(job)) return;
  queue.push(job);
  scheduleQueue();
}

function createJob(record) {
  const completion = createDeferred();
  const job = {
    key: record.key,
    record,
    completion,
    controller: null,
    cancelRequest: null,
    timeoutId: null,
    timedOut: false,
    running: false,
    slotHeld: false,
    uploadPermit: null,
  };
  jobs.set(record.key, job);
  return job;
}

/**
 * Persists a capture before its HTTP request starts. The UI may leave the
 * camera as soon as `persisted` resolves with `ok: true`; `completion` belongs
 * to the app-wide worker and survives page changes.
 */
export function enqueuePhotoUpload({
  vehicleId,
  category,
  step,
  blob,
  previewUrl = null,
  ownerEmail,
  previousImageUrl = null,
  imageSource = "camera",
  fileName,
  contentType,
  observedUploadId = null,
  observedGeneration = null,
  observedBaseMainUrl = null,
}) {
  if (!blob) throw new TypeError("enqueuePhotoUpload benötigt einen Blob.");
  const normalizedOwner = normalizeEmail(ownerEmail);
  // A capture can beat the queue manager's first React effect immediately
  // after login. Bind that first job to its authenticated owner now so the
  // subsequent registry initialization does not reset and temporarily orphan
  // the just-persisted upload until the next page load.
  if (!currentOwnerEmail && normalizedOwner) {
    currentOwnerEmail = normalizedOwner;
    publishRegistryStatus({ ownerEmail: normalizedOwner });
  }
  const key = makePhotoUploadKey(vehicleId, category, step);
  const previousRecord = records.get(key);
  const previousRecordGeneration = Number(previousRecord?.generation || 0);
  const localPredecessorUploadId =
    previousRecord?.state === "pending" &&
    previousRecord.serverRequestStarted === true
      ? previousRecord.uploadId
      : null;
  const localPredecessorGeneration =
    localPredecessorUploadId && previousRecordGeneration > 0
      ? previousRecordGeneration
      : null;
  const hasObservedPreviousRecord =
    previousRecord?.uploadId &&
    Number.isSafeInteger(previousRecordGeneration) &&
    previousRecordGeneration > 0;
  const effectiveObservedUploadId = hasObservedPreviousRecord
    ? previousRecord.uploadId
    : observedUploadId;
  const effectiveObservedGeneration = hasObservedPreviousRecord
    ? previousRecordGeneration
    : observedGeneration;
  const effectiveObservedBaseMainUrl = hasObservedPreviousRecord
    ? previousRecord.state === "committed"
      ? previousRecord.imageUrl || null
      : previousRecord.previousImageUrl || null
    : observedBaseMainUrl;
  const capturedAt = Math.max(
    Date.now(),
    Number(previousRecord?.capturedAt || 0) + 1
  );
  const record = {
    key,
    ownerEmail: normalizedOwner,
    vehicleId: String(vehicleId || ""),
    category: String(category || ""),
    step: String(step || ""),
    blob,
    fileName: fileName || `${step}.jpg`,
    contentType: contentType || blob.type || "image/jpeg",
    imageSource,
    apiBaseUrl,
    uploadId: createUploadId(),
    capturedAt,
    generation: null,
    localPredecessorUploadId,
    localPredecessorGeneration,
    observedUploadId: effectiveObservedUploadId || null,
    observedGeneration:
      Number(effectiveObservedGeneration || 0) > 0
        ? Number(effectiveObservedGeneration)
        : null,
    observedBaseMainUrl: effectiveObservedBaseMainUrl || null,
    previousImageUrl,
    imageUrl: null,
    state: "pending",
    phase: "persisting",
    attempts: 0,
    nextRetryAt: null,
    serverRequestStarted: false,
    retryable: true,
    errorName: null,
    errorCode: null,
    errorMessage: null,
    httpStatus: null,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    persisted: true,
  };

  const oldJob = jobs.get(key);
  if (oldJob) abortJob(oldJob);
  clearRetryTimer(key);
  records.set(key, record);
  const job = createJob(record);
  publishEntry(key, entryFromRecord(record, previewUrl));

  const persisted = (async () => {
    try {
      if (!registryStatus.persistenceAvailable) {
        const error = new Error(
          "IndexedDB ist vorübergehend nicht verfügbar."
        );
        error.name = "PhotoUploadStorageUnavailableError";
        throw error;
      }
      const didPersist = await persistPhotoUploadRecord(record);
      if (!isJobCurrent(job) || !didPersist) {
        if (!didPersist) await reconcileAfterCasLoss(job);
        else settle(job, { ok: false, superseded: true });
        return { ok: false, superseded: true };
      }
      record.phase = "queued";
      record.updatedAt = Date.now();
      publishEntry(key, entryFromRecord(record, previewUrl));
      enqueueJob(job);
      return { ok: true };
    } catch (error) {
      console.error("Foto konnte nicht in IndexedDB gespeichert werden:", error);
      record.persisted = false;
      record.phase = "queued";
      publishRegistryStatus({ persistenceAvailable: false });
      publishEntry(key, entryFromRecord(record, previewUrl));
      enqueueJob(job);
      return { ok: false, error };
    }
  })();

  return { persisted, completion: job.completion.promise };
}

export function getPhotoUploadEntry(
  uploadSnapshot,
  { vehicleId, category, step }
) {
  const direct = uploadSnapshot?.[makePhotoUploadKey(vehicleId, category, step)];
  if (direct) return direct;
  return Object.values(uploadSnapshot || {}).find(
    (entry) =>
      String(entry?.vehicleId) === String(vehicleId) &&
      (!category || entry?.category === category) &&
      entry?.step === step
  );
}

export function mergePendingPhotoUploadsIntoCarData(
  carData,
  { vehicleId, uploadSnapshot = snapshot } = {}
) {
  if (!carData) return carData;
  const entries = Object.values(uploadSnapshot).filter(
    (entry) =>
      String(entry?.vehicleId) === String(vehicleId) &&
      NAVIGATION_STATES.has(entry?.state)
  );
  if (!entries.length) return carData;

  const carImages = { ...(carData.carImages || {}) };
  entries.forEach((entry) => {
    carImages[entry.step] =
      entry.state === "superseded" && carImages[entry.step]
        ? carImages[entry.step]
        : entry.previewUrl || entry.imageUrl || carImages[entry.step];
  });
  return { ...carData, carImages };
}

export function isPhotoUploadEntryServerConfirmed(entry, carData) {
  return resolvePhotoUploadServerState(entry, carData).kind === "exact";
}

function isServerUploadNewer(entry, version) {
  const entryGeneration = Number(entry?.generation || 0);
  const serverGeneration = Number(version?.generation || 0);
  if (entryGeneration > 0) return serverGeneration > entryGeneration;

  const entryCapturedAt = Number(entry?.capturedAt || 0);
  const serverCapturedAt = Number(version?.capturedAt || 0);
  if (entryCapturedAt <= 0 || serverCapturedAt <= 0) return false;
  if (serverCapturedAt !== entryCapturedAt) {
    return serverCapturedAt > entryCapturedAt;
  }
  return (
    String(version?.uploadId || "").localeCompare(
      String(entry?.uploadId || "")
    ) > 0
  );
}

export function resolvePhotoUploadServerState(entry, carData) {
  if (!entry || !carData) return { kind: "none" };
  const version = carData?.photoUploadVersions?.carImages?.[entry.step];
  const currentImageUrl = carData?.carImages?.[entry.step] || null;
  const entryGeneration = Number(entry.generation || 0);
  const serverGeneration = Number(version?.generation || 0);
  const stableMainCommit = Boolean(
    version?.status === "main_uploaded" &&
      version?.mainUrl &&
      currentImageUrl === version.mainUrl &&
      serverGeneration > 0
  );
  const stableDelete = Boolean(
    version?.status === "deleted" &&
      !currentImageUrl &&
      serverGeneration > 0
  );

  // The HTTP response can be lost after the atomic server write. In that case
  // Firestore is authoritative when it identifies this exact completed upload.
  const exactUploadCompletedOnServer = Boolean(
    entry.uploadId &&
      version?.uploadId === entry.uploadId &&
      stableMainCommit &&
      (!entryGeneration || entryGeneration === serverGeneration)
  );
  if (exactUploadCompletedOnServer) {
    return {
      kind: "exact",
      imageUrl: version.mainUrl,
      generation: serverGeneration,
      serverUploadId: version.uploadId,
    };
  }

  if (
    entry.uploadId &&
    version?.uploadId === entry.uploadId &&
    stableDelete &&
    (!entryGeneration || entryGeneration === serverGeneration)
  ) {
    return {
      kind: "deleted",
      imageUrl: null,
      generation: serverGeneration,
      serverUploadId: version.uploadId,
    };
  }

  const sameVersionWasChangedExternally = Boolean(
    version?.uploadId === entry.uploadId &&
      ((version?.status === "main_uploaded" &&
        version?.mainUrl &&
        currentImageUrl !== version.mainUrl) ||
        (version?.status === "pending" &&
          currentImageUrl !== (version.baseMainUrl || null)))
  );
  if (sameVersionWasChangedExternally) {
    return {
      kind: currentImageUrl ? "superseded" : "deleted",
      imageUrl: currentImageUrl,
      generation: serverGeneration || null,
      serverUploadId: version.uploadId,
    };
  }

  if (
    version?.uploadId &&
    version.uploadId !== entry.uploadId &&
    isServerUploadNewer(entry, version)
  ) {
    if (stableMainCommit) {
      return {
        kind: "superseded",
        imageUrl: version.mainUrl,
        generation: serverGeneration,
        serverUploadId: version.uploadId,
      };
    }
    if (stableDelete) {
      return {
        kind: "deleted",
        imageUrl: null,
        generation: serverGeneration,
        serverUploadId: version.uploadId,
      };
    }
  }

  return { kind: "none" };
}

export async function acknowledgePhotoUploadServerConfirmation({
  vehicleId,
  category,
  step,
  uploadId,
  imageUrl = null,
  generation = null,
}) {
  const key = makePhotoUploadKey(vehicleId, category, step);
  const record = records.get(key);
  if (
    !record ||
    !uploadId ||
    record.uploadId !== uploadId ||
    record.state === "deleting"
  ) {
    return false;
  }
  const confirmedImageUrl =
    (typeof imageUrl === "string" && imageUrl.trim()) ||
    record.imageUrl ||
    null;
  const confirmedGeneration =
    Number(generation || record.generation || 0) || null;
  if (!confirmedImageUrl) return false;

  const terminalRecord = {
    ...record,
    blob: null,
    imageUrl: confirmedImageUrl,
    generation: confirmedGeneration,
    state: "confirmed",
    phase: null,
    retryable: false,
    errorCode: null,
    errorMessage: null,
    nextRetryAt: null,
    updatedAt: Date.now(),
  };
  // Firestore is the source of truth. Terminalise synchronously before touching
  // IndexedDB so an unavailable or hanging private-mode store cannot delay the
  // UI/completion. JavaScript cannot interleave a retake before this memory
  // update; any later retake gets a new uploadId and the IndexedDB CAS below
  // cannot overwrite it.
  records.set(key, terminalRecord);
  const activeJob = jobs.get(key);
  if (activeJob?.record?.uploadId === uploadId) {
    abortJob(activeJob, {
      ok: true,
      imageUrl: confirmedImageUrl,
      generation: confirmedGeneration,
      serverConfirmed: true,
    });
    if (jobs.get(key) === activeJob) jobs.delete(key);
  }
  clearRetryTimer(key);
  releasePreviewUrl(key);
  removeEntry(key);

  void persistPhotoUploadRecord(terminalRecord)
    .then((persisted) => {
      if (!persisted) return;
      const current = records.get(key);
      if (current?.uploadId === uploadId && current.state === "confirmed") {
        current.persisted = true;
      }
    })
    .catch((error) => {
      console.error("Upload-Bestätigung konnte nicht gespeichert werden:", error);
      publishRegistryStatus({ persistenceAvailable: false });
    });
  return true;
}

export function reconcilePhotoUploadServerReplacement({
  vehicleId,
  category,
  step,
  uploadId,
  serverState,
}) {
  if (!["superseded", "deleted"].includes(serverState?.kind)) return false;
  const key = makePhotoUploadKey(vehicleId, category, step);
  const record = records.get(key);
  if (
    !record ||
    !uploadId ||
    record.uploadId !== uploadId ||
    record.state === "deleting"
  ) {
    return false;
  }

  const terminalRecord = {
    ...record,
    blob: null,
    imageUrl: serverState.imageUrl || null,
    generation:
      Number(serverState.generation || record.generation || 0) || null,
    state: serverState.kind === "deleted" ? "deleted" : "confirmed",
    phase: null,
    retryable: false,
    currentUploadId: serverState.serverUploadId || null,
    currentGeneration: Number(serverState.generation || 0) || null,
    errorCode: null,
    errorMessage: null,
    nextRetryAt: null,
    updatedAt: Date.now(),
  };

  records.set(key, terminalRecord);
  const activeJob = jobs.get(key);
  if (activeJob?.record?.uploadId === uploadId) {
    abortJob(activeJob, {
      ok: false,
      externalReplacement: serverState.kind === "superseded",
      deleted: serverState.kind === "deleted",
      serverReconciled: true,
    });
    if (jobs.get(key) === activeJob) jobs.delete(key);
  }
  clearRetryTimer(key);
  releasePreviewUrl(key);
  removeEntry(key);

  void persistPhotoUploadRecord(terminalRecord)
    .then((persisted) => {
      if (!persisted) return;
      const current = records.get(key);
      if (current?.uploadId === uploadId) current.persisted = true;
    })
    .catch((error) => {
      console.error(
        "Extern ersetzter Foto-Upload konnte nicht lokal abgeglichen werden:",
        error
      );
      publishRegistryStatus({ persistenceAvailable: false });
    });
  return true;
}

export async function preparePhotoUploadDelete({
  vehicleId,
  category = null,
  step,
  expectedImageUrl = null,
}) {
  const entry = getPhotoUploadEntry(snapshot, { vehicleId, category, step });
  if (!entry) {
    return {
      found: false,
      localOnly: false,
      expectedImageUrl,
      localAttemptGuarded: Boolean(expectedImageUrl),
    };
  }
  const key = entry.key;
  const memoryRecord = records.get(key) || { ...entry, key };

  let deletingRecord = memoryRecord;
  if (memoryRecord.persisted !== false) {
    try {
      const marked = await markPhotoUploadDeleting(key, entry.uploadId);
      if (!marked.ok) {
        adoptStoredRecord(marked.current);
        return {
          found: false,
          conflict: true,
          localOnly: false,
          expectedImageUrl,
          localAttemptGuarded: true,
        };
      }
      deletingRecord = { ...marked.record, persisted: true };
    } catch (error) {
      console.error("Foto-Löschung konnte nicht vorbereitet werden:", error);
      publishRegistryStatus({ persistenceAvailable: false });
      deletingRecord = {
        ...memoryRecord,
        state: "deleting",
        phase: null,
        persisted: false,
      };
    }
  } else {
    deletingRecord = {
      ...memoryRecord,
      state: "deleting",
      phase: null,
    };
  }

  const job = jobs.get(key);
  if (job) abortJob(job, { ok: false, deleted: true });
  if (jobs.get(key) === job) jobs.delete(key);
  clearRetryTimer(key);
  records.set(key, deletingRecord);
  publishEntry(
    key,
    entryFromRecord(deletingRecord, snapshot[key]?.previewUrl || null)
  );

  return {
    found: true,
    category: entry.category,
    localOnly:
      deletingRecord.serverRequestStarted !== true &&
      !deletingRecord.imageUrl &&
      !deletingRecord.previousImageUrl,
    expectedUploadId: deletingRecord.uploadId || null,
    expectedGeneration: deletingRecord.generation || null,
    expectedCapturedAt: deletingRecord.capturedAt || null,
    expectedImageUrl:
      deletingRecord.imageUrl ||
      deletingRecord.previousImageUrl ||
      expectedImageUrl ||
      null,
    localAttemptGuarded: true,
  };
}

export async function confirmPhotoUploadDelete({
  vehicleId,
  category = null,
  step,
  uploadId = null,
  serverConfirmed = false,
}) {
  const entry = getPhotoUploadEntry(snapshot, { vehicleId, category, step });
  if (!entry || (uploadId && entry.uploadId !== uploadId)) return false;
  const key = entry.key;
  const record = records.get(key) || { ...entry, key };
  if (record.uploadId !== entry.uploadId) return false;

  const terminalRecord = {
    ...record,
    blob: null,
    state: "deleted",
    phase: null,
    updatedAt: Date.now(),
  };
  if (record.persisted !== false) {
    try {
      const persisted = await tombstonePhotoUploadRecord(terminalRecord);
      if (!persisted) return false;
    } catch (error) {
      console.error("Foto-Löschung konnte nicht lokal bestätigt werden:", error);
      publishRegistryStatus({ persistenceAvailable: false });
      if (!serverConfirmed) return false;
    }
  }
  if (records.get(key)?.uploadId !== record.uploadId) return false;
  records.set(key, terminalRecord);
  releasePreviewUrl(key);
  removeEntry(key);
  return true;
}

export async function rollbackPhotoUploadDelete({
  vehicleId,
  category = null,
  step,
  uploadId = null,
}) {
  const entry = getPhotoUploadEntry(snapshot, { vehicleId, category, step });
  if (!entry || (uploadId && entry.uploadId !== uploadId)) return false;
  const key = entry.key;
  const record = records.get(key) || { ...entry, key };
  if (record.uploadId !== entry.uploadId || record.state !== "deleting") {
    return false;
  }

  let restoredRecord;
  if (record.persisted !== false) {
    try {
      const restored = await restorePhotoUploadAfterDeleteFailure(
        key,
        record.uploadId
      );
      if (!restored.ok) {
        adoptStoredRecord(restored.current);
        return false;
      }
      restoredRecord = { ...restored.record, persisted: true };
    } catch (error) {
      console.error("Foto-Löschung konnte nicht zurückgerollt werden:", error);
      publishRegistryStatus({ persistenceAvailable: false });
    }
  }
  if (!restoredRecord) {
    restoredRecord = { ...record };
    restoredRecord.state =
      record.stateBeforeDelete || (record.imageUrl ? "committed" : "pending");
    restoredRecord.phase =
      restoredRecord.state === "pending"
        ? record.phaseBeforeDelete || "queued"
        : null;
    delete restoredRecord.stateBeforeDelete;
    delete restoredRecord.phaseBeforeDelete;
  }
  if (records.get(key)?.uploadId !== record.uploadId) return false;
  records.set(key, restoredRecord);
  publishEntry(
    key,
    entryFromRecord(restoredRecord, snapshot[key]?.previewUrl || null)
  );
  if (restoredRecord.state === "pending") {
    const oldJob = jobs.get(key);
    if (oldJob) abortJob(oldJob);
    enqueueJob(createJob(restoredRecord));
  }
  return true;
}

export function hasUnsafeVolatilePhotoUploads(uploadSnapshot = snapshot) {
  return Object.values(uploadSnapshot).some(
    (entry) =>
      entry?.hasLocalBlob === true &&
      ["pending", "failed"].includes(entry?.state)
  );
}

export async function awaitVehiclePhotoUploads(vehicleId) {
  if (hydrationPromise) await hydrationPromise;
  if (!registryStatus.hydrated) {
    return {
      ok: false,
      failedEntries: [],
      pendingEntries: [
        {
          vehicleId,
          state: "pending",
          phase: registryStatus.hydrated ? "persistence_unavailable" : "hydrating",
        },
      ],
    };
  }

  if (
    currentOwnerEmail &&
    !registryStatus.storageDefinitelyUnavailable
  ) {
    try {
      const storedRecords = await loadPhotoUploadRecordsByOwner(currentOwnerEmail);
      storedRecords
        .filter((record) => String(record.vehicleId) === String(vehicleId))
        .forEach(adoptStoredRecord);
    } catch (error) {
      console.error("Foto-Uploads konnten vor Abschluss nicht geprüft werden:", error);
      return {
        ok: false,
        failedEntries: [],
        pendingEntries: [
          { vehicleId, state: "pending", phase: "persistence_unavailable" },
        ],
      };
    }
  }
  const entries = Object.values(snapshot).filter(
    (entry) => String(entry?.vehicleId) === String(vehicleId)
  );
  const failedEntries = entries.filter((entry) => entry.state === "failed");
  const pendingEntries = entries.filter((entry) =>
    ["pending", "deleting", "superseded"].includes(entry.state)
  );
  return {
    ok: failedEntries.length === 0 && pendingEntries.length === 0,
    failedEntries,
    pendingEntries,
  };
}

function resetRuntime(ownerEmail) {
  hydrationGeneration += 1;
  hydrationPromise = null;
  // Clear queued work before releasing running slots so abortJob cannot start
  // another queued request while the registry is being reset.
  queue.length = 0;
  jobs.forEach((job) => abortJob(job));
  jobs.clear();
  retryTimers.forEach((timer) => clearTimeout(timer));
  retryTimers.clear();
  activeUploads = 0;
  ownedPreviewUrls.forEach((url) => globalThis.URL?.revokeObjectURL?.(url));
  ownedPreviewUrls.clear();
  records.clear();
  snapshot = Object.freeze({});
  currentOwnerEmail = ownerEmail;
  registryStatus = Object.freeze({
    hydrated: false,
    ownerEmail,
    persistenceAvailable: true,
    storageDefinitelyUnavailable: false,
  });
  emit();
}

export async function initializePhotoUploadRegistry({
  apiBaseUrl: nextApiBaseUrl,
  ownerEmail,
} = {}) {
  apiBaseUrl = String(nextApiBaseUrl || apiBaseUrl || "").replace(/\/+$/, "");
  const normalizedOwner = normalizeEmail(ownerEmail);

  if (normalizedOwner !== currentOwnerEmail) resetRuntime(normalizedOwner);
  if (
    registryStatus.hydrated &&
    registryStatus.persistenceAvailable &&
    registryStatus.ownerEmail === normalizedOwner
  ) {
    return registryStatus;
  }
  if (hydrationPromise) return hydrationPromise;

  publishRegistryStatus({ hydrated: false, ownerEmail: normalizedOwner });
  const generation = hydrationGeneration;
  hydrationPromise = (async () => {
    try {
      const storedRecords = normalizedOwner
        ? await loadPhotoUploadRecordsByOwner(normalizedOwner)
        : [];
      if (generation !== hydrationGeneration) return registryStatus;
      for (const storedRecord of storedRecords) {
        let hydratedRecord = storedRecord;
        if (storedRecord.state === "deleting") {
          const restored = await restorePhotoUploadAfterDeleteFailure(
            storedRecord.key,
            storedRecord.uploadId
          );
          hydratedRecord = restored.ok ? restored.record : restored.current;
        }
        if (!hydratedRecord) continue;
        adoptStoredRecord({ ...hydratedRecord, persisted: true });
      }
      await persistVolatileRecords(normalizedOwner);
      publishRegistryStatus({
        hydrated: true,
        ownerEmail: normalizedOwner,
        persistenceAvailable: true,
        storageDefinitelyUnavailable: false,
      });
    } catch (error) {
      if (generation !== hydrationGeneration) return registryStatus;
      console.error("Gespeicherte Foto-Uploads konnten nicht geladen werden:", error);
      const readIsKnownEmpty =
        typeof globalThis.indexedDB === "undefined" ||
        ["SecurityError", "NotSupportedError"].includes(
          error?.name
        );
      publishRegistryStatus({
        hydrated: true,
        ownerEmail: normalizedOwner,
        persistenceAvailable: false,
        storageDefinitelyUnavailable:
          registryStatus.storageDefinitelyUnavailable || readIsKnownEmpty,
      });
    } finally {
      if (generation === hydrationGeneration) hydrationPromise = null;
    }

    if (!onlineListenerInstalled && typeof window !== "undefined") {
      onlineListenerInstalled = true;
      window.addEventListener("online", () => {
        jobs.forEach((job) => {
          if (!isJobCurrent(job)) return;
          clearRetryTimer(job.key);
          enqueueJob(job);
        });
      });
    }
    return registryStatus;
  })();

  return hydrationPromise;
}

export async function __resetPhotoUploadRegistryForTests() {
  resetRuntime(null);
  registryStatus = Object.freeze({
    hydrated: false,
    ownerEmail: null,
    persistenceAvailable: true,
    storageDefinitelyUnavailable: false,
  });
  apiBaseUrl = "";
  uploadTimeoutOverrideMs = null;
  loadPhotoUploadRecordsByOwner = getPhotoUploadRecordsByOwner;
  persistPhotoUploadRecord = putPhotoUploadRecordIfCurrent;
}

export function __setPhotoUploadTimeoutForTests(timeoutMs) {
  uploadTimeoutOverrideMs = Math.max(1, Number(timeoutMs) || 1);
}

export function __setPhotoUploadStoreAdaptersForTests({
  loadRecordsByOwner,
  persistRecord,
} = {}) {
  loadPhotoUploadRecordsByOwner =
    typeof loadRecordsByOwner === "function"
      ? loadRecordsByOwner
      : getPhotoUploadRecordsByOwner;
  persistPhotoUploadRecord =
    typeof persistRecord === "function"
      ? persistRecord
      : putPhotoUploadRecordIfCurrent;
}
